import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';

import { setPlayRingtoneSetting } from 'flow/actions/phone-actions';
import { fetchRingtoneSettings } from 'flow/actions/ringtone-actions';
import Divider from 'flow/components/ui-components/Divider';
import RadioButton from 'flow/components/ui-components/RadioButton';
import RangeSlider from 'flow/components/ui-components/RangeSlider';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';

import {
    getPreferredRingtoneVolume,
    savePreferredRingtoneVolume,
} from 'flow/utils/browser-utils';
import { PlayRingtoneSetting } from 'flow/utils/call-utils';
import { getConnectedMediaDeviceOfKindWithId } from 'flow/utils/media-utils';
import { generatePath } from 'flow/utils/router-utils';
import { RingTonePlayer } from 'resources/ringtone-player';

import Page from '../Page';
import SettingRow from '../SettingRow';

const Ringtone = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const [ringtoneVolume, setRingtoneVolume] = useState(
        getPreferredRingtoneVolume(),
    );

    const selectedRingtoneName = useAppSelector((state) =>
        state.ringtones.getIn(['settings', 'name']),
    );

    const mediaDevices = useAppSelector((state) =>
        state.headsets.get('connectedMediaDevices'),
    );

    const preferredAudioOutputId = useAppSelector((state) =>
        state.headsets.get('preferredRingtoneAudioOutputId'),
    );

    const playRingtoneSetting = useAppSelector((state) =>
        PlayRingtoneSetting.get(state.phone.get('playRingtoneSetting')),
    );

    const preferredAudioOutput = getConnectedMediaDeviceOfKindWithId(
        mediaDevices,
        'audiooutput',
        preferredAudioOutputId,
    );

    useEffect(() => {
        dispatch(fetchRingtoneSettings());
    }, [dispatch]);

    const handleChangeRingtoneVolume = (value: number) => {
        const formattedValue = value / 100;
        savePreferredRingtoneVolume(formattedValue);
        RingTonePlayer.setVolume(formattedValue);
        setRingtoneVolume(formattedValue);
    };

    const disableRingtoneOptions =
        !PlayRingtoneSetting.RING.is(playRingtoneSetting);

    return (
        <Page
            header={'media.settings.ringtonevolume'}
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.softphone'], {
                        extensionKey,
                    }),
                )
            }
        >
            {PlayRingtoneSetting.enums.map(
                (option: typeof PlayRingtoneSetting) => (
                    <RadioButton
                        key={option.value}
                        name={option.value}
                        onChange={() =>
                            dispatch(
                                setPlayRingtoneSetting(
                                    PlayRingtoneSetting.get(option),
                                ),
                            )
                        }
                        checked={!!playRingtoneSetting?.is(option)}
                    >
                        {i18next.t(`settings.softphone.${option.value}`)}
                    </RadioButton>
                ),
            )}

            <Divider />

            <RouteLink
                to={routes['settings.softphone.ringtone.select-ringtone']}
                description={selectedRingtoneName}
                disabled={disableRingtoneOptions}
            >
                {i18next.t('media.settings.ringtone')}
            </RouteLink>

            <SettingRow
                disabled={disableRingtoneOptions}
                cta={
                    <RangeSlider
                        handleChange={(e) => {
                            handleChangeRingtoneVolume(
                                parseFloat(e.target.value),
                            );
                        }}
                        preferredValue={ringtoneVolume * 100}
                        disabled={disableRingtoneOptions}
                        ariaLabel={i18next.t('media.settings.volume')}
                    />
                }
            >
                {i18next.t('media.settings.volume')}
            </SettingRow>

            <Divider />

            <RouteLink
                to={routes['settings.softphone.ringtone.audio-output']}
                description={
                    preferredAudioOutput
                        ? preferredAudioOutput.label
                        : i18next.t(
                              'media.settings.ringtone.additionaloutput.none',
                          )
                }
                disabled={disableRingtoneOptions}
            >
                {i18next.t('media.settings.ringtone.additionaloutput')}
            </RouteLink>

            <p className="p-2 text-xs text-gray-500">
                {i18next.t('media.settings.ringtone.additionaloutput.helper')}
            </p>
        </Page>
    );
};

export default Ringtone;
